import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import { mirrorAuthIdentity, sameMirrorAuthIdentity, type MirrorAuthIdentity } from "@/lib/mirrorAuthSession";
import { microsoftTodoImportCopy } from "@/lib/microsoftTodoImportCopy";
import { publishTaskRefresh } from "@/lib/taskChanges";
import MicrosoftTodoImportPanel from "@/components/connections/MicrosoftTodoImportPanel";

export interface MicrosoftTodoImportLauncherProps {
  enabled: boolean;
  ownerId: string | null;
  className?: string;
  onImported?: () => void;
}
interface Opening {
  epoch: number;
  ownerId: string;
  identity: MirrorAuthIdentity | null;
  observed: MirrorAuthIdentity | null;
  revoked: boolean;
  unsubscribe: () => void;
}
interface Origin { ownerId: string; authSessionId: string; epoch: number }

/** Explicit entry point. Opening authenticates the actor but never loads provider lists. */
export default function MicrosoftTodoImportLauncher({
  enabled, ownerId, className, onImported,
}: MicrosoftTodoImportLauncherProps) {
  const { lang } = useLanguage();
  const copy = microsoftTodoImportCopy[lang];
  const [busy, setBusy] = useState(false);
  const [origin, setOrigin] = useState<Origin | null>(null);
  const [error, setError] = useState(false);
  const epoch = useRef(0);
  const active = useRef<Opening | null>(null);
  const current = useRef({ enabled, ownerId, onImported });
  current.current = { enabled, ownerId, onImported };
  const invalidate = useCallback((update = true) => {
    epoch.current++;
    const previous = active.current;
    active.current = null;
    if (previous) { previous.revoked = true; previous.unsubscribe(); }
    if (update) { setBusy(false); setOrigin(null); }
  }, []);
  useLayoutEffect(() => {
    invalidate(); setError(false);
    return () => invalidate(false);
  }, [enabled, ownerId, invalidate]);
  const isCurrent = (lease: Opening) => active.current === lease && !lease.revoked &&
    epoch.current === lease.epoch && current.current.enabled && current.current.ownerId === lease.ownerId;
  const close = () => { invalidate(); setError(false); };
  const launch = async () => {
    const owner = current.current.ownerId;
    if (!current.current.enabled || !owner || active.current) return;
    const lease: Opening = {
      epoch: ++epoch.current, ownerId: owner, identity: null, observed: null,
      revoked: false, unsubscribe: () => {},
    };
    active.current = lease; setBusy(true); setError(false);
    try {
      // Subscribe before either await. All observations must agree with the eventual origin,
      // so an actor/SID A -> B -> A during opening permanently revokes this click.
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        if (!isCurrent(lease)) return;
        const next = mirrorAuthIdentity(session);
        if (!next || next.ownerId !== owner ||
          lease.observed && !sameMirrorAuthIdentity(lease.observed, next) ||
          lease.identity && !sameMirrorAuthIdentity(lease.identity, next)) {
          invalidate(); setError(true);
          return;
        }
        lease.observed = next;
      });
      lease.unsubscribe = () => subscription.unsubscribe();
      if (!isCurrent(lease)) { lease.unsubscribe(); return; }
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (!isCurrent(lease)) return;
      if (userError || user?.id !== owner) throw new Error("invalid_session");
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (!isCurrent(lease)) return;
      const identity = mirrorAuthIdentity(session);
      if (sessionError || !identity || identity.ownerId !== owner ||
        lease.observed && !sameMirrorAuthIdentity(lease.observed, identity)) throw new Error("invalid_session");
      lease.identity = identity;
      lease.observed = identity;
      setOrigin({ ownerId: owner, authSessionId: identity.authSessionId, epoch: lease.epoch });
    } catch {
      if (isCurrent(lease)) { invalidate(); setError(true); }
    } finally {
      // A late auth read cannot unlock a newer click or reopen a closed panel.
      if (active.current === lease) setBusy(false);
    }
  };
  const imported = () => {
    const lease = active.current;
    if (!origin || !lease?.identity || !isCurrent(lease) || lease.epoch !== origin.epoch ||
      lease.identity.ownerId !== origin.ownerId || lease.identity.authSessionId !== origin.authSessionId ||
      !sameMirrorAuthIdentity(lease.identity, lease.observed)) return;
    // The actual panel calls this only after validating canonical apply evidence and fresh auth.
    publishTaskRefresh(origin.ownerId);
    if (isCurrent(lease)) current.current.onImported?.();
  };

  if (!enabled || !ownerId) return null;
  return <div className={className}>
    <button type="button" disabled={busy} aria-busy={busy} onClick={() => { void launch(); }}
      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-sm font-medium disabled:opacity-50">
      {busy && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}{copy.title}
    </button>
    {busy && <button type="button" onClick={close}
      className="ml-2 inline-flex min-h-11 items-center rounded-lg border border-border px-3 py-2.5 text-sm font-medium">{copy.close}</button>}
    {error && <p role="alert" className="mt-2 text-sm text-destructive">{copy.sessionChanged}</p>}
    {origin && <MicrosoftTodoImportPanel open={!!active.current && isCurrent(active.current)} enabled={enabled} ownerId={origin.ownerId}
      authSessionId={origin.authSessionId} onClose={close} onImported={imported} />}
  </div>;
}
